// Time-entry corrections — the employee asks, a senior decides.
//
// Everything enforced here is enforced again in Postgres
// (20260825100000_time_corrections.sql): the checks below exist to give a useful
// message before a round-trip, not to be the boundary. The trigger server-stamps
// work_date, current_seconds, approver and decided_at, so the values sent from
// here for those fields are advisory at best.
//
// Kept out of the screens deliberately: colocated tests mock this module rather
// than a chainable PostgREST builder (see components/timer.test.tsx).
import { supabase } from './supabase';
import { fmtDuration } from './duration';
import { write } from './write';

export type CorrectionStatus = 'pending' | 'approved' | 'rejected';

export type Correction = {
  id: string;
  profile_id: string;
  /** NULL = "I forgot to clock in": approval creates the entry. */
  entry_id: string | null;
  work_date: string;
  current_seconds: number;
  requested_seconds: number;
  /** Org-local 'HH:MM:SS' the person asked for (20261009100000). With
   *  requested_out set, requested_seconds was derived from the times. */
  requested_in?: string | null;
  requested_out?: string | null;
  reason: string;
  status: CorrectionStatus;
  approver: string | null;
  decided_at: string | null;
  /** Why it was declined; null unless status is 'rejected' (20260930130000).
   *  Optional so a row built by hand (a test, an optimistic insert) can leave it out. */
  decline_reason?: string | null;
  created_at: string;
};

/** A pending row in the approver's queue, with the requester's name resolved. */
export type PendingCorrection = Correction & { full_name: string | null };

// decline_reason is readable by the requester and the deciders alike (tc_read).
// HR's queue shows it, and so does the requester's My requests.
const SELECT =
  'id, profile_id, entry_id, work_date, current_seconds, requested_seconds, requested_in, requested_out, reason, status, approver, decided_at, decline_reason, created_at';

/**
 * The size of a requested change, signed: "+30m", "−1h 15m". A duration
 * clamps at zero (lib/duration); a CHANGE has a direction, so the sign is added
 * here. U+2212, not a hyphen.
 */
export const fmtDelta = (seconds: number): string =>
  `${seconds < 0 ? '−' : '+'}${fmtDuration(Math.abs(seconds), 'hm')}`;

/**
 * What a correction asks for, in one line: the times when it named them
 * ("out 18:30 · 8h 00m → 7h 30m", "07:00–09:30 · 2h 30m"), else the hours.
 */
export const correctionSummary = (
  c: Pick<
    Correction,
    'entry_id' | 'current_seconds' | 'requested_seconds' | 'requested_in' | 'requested_out'
  >,
): string => {
  const hm = (s: number) => fmtDuration(s, 'hm');
  const t = (v: string) => v.slice(0, 5);
  if (c.requested_out && !c.entry_id && c.requested_in)
    return `${t(c.requested_in)}–${t(c.requested_out)} · ${hm(c.requested_seconds)}`;
  const hours = `${c.entry_id ? hm(c.current_seconds) : 'no entry'} → ${hm(c.requested_seconds)}`;
  if (!c.requested_out) return hours;
  return `${c.requested_in ? `in ${t(c.requested_in)}, ` : ''}out ${t(c.requested_out)} · ${hours}`;
};

export async function fileCorrection(input: {
  profileId: string;
  /** null files a missed session against workDate instead. */
  entryId: string | null;
  workDate: string;
  /** Org-local 'HH:MM'; null keeps the entry's own check-in. */
  checkIn: string | null;
  /** Org-local 'HH:MM'. The server derives the hours from the two, less breaks. */
  checkOut: string;
  reason: string;
}): Promise<void> {
  if (!supabase) return;
  const { error } = await supabase.from('time_corrections').insert({
    profile_id: input.profileId,
    entry_id: input.entryId,
    work_date: input.workDate,
    // Derived by enforce_time_correction from the times; the column is not null.
    requested_seconds: 0,
    requested_in: input.checkIn,
    requested_out: input.checkOut,
    reason: input.reason.trim(),
  });
  if (error) throw error;
}

/**
 * Cancel while pending. tc_delete refuses once decided, and that refusal is a
 * zero-row delete with no error: write() makes it one, so a request HR decided
 * a moment ago is not reported cancelled.
 */
export async function cancelCorrection(id: string): Promise<void> {
  if (!supabase) return;
  const r = await write(
    supabase.from('time_corrections').delete().eq('id', id).eq('status', 'pending'),
  );
  if (r.ok) return;
  throw r.reason === 'refused'
    ? { message: 'It has already been decided, so it can’t be cancelled. Reload to see.' }
    : r.error;
}

/**
 * The viewer's own requests, newest first. `failed` marks a read that errored:
 * still an array, so a caller that has not learned to ask renders what it did
 * for an empty read, and one that has can say the list is unknown rather than
 * empty (UI-CONVENTIONS §7). The shape loadSummaries answers in.
 */
export async function listMine(uid: string): Promise<Correction[] & { failed?: true }> {
  if (!supabase) return [];
  const { data, error } = await supabase
    .from('time_corrections')
    .select(SELECT)
    .eq('profile_id', uid)
    .order('created_at', { ascending: false })
    .limit(20);
  if (error) return Object.assign([], { failed: true as const });
  return (data as Correction[]) ?? [];
}

/**
 * Every correction the viewer may see, newest first — pending AND decided.
 *
 * Replaced a listPending() that answered "what needs me now", which is the wrong
 * question for a queue with Approved/Denied/All filters: a correction vanished
 * the instant it was decided, so there was nowhere at all to see one that had
 * been approved. Same RLS, same shape, no status filter. (compHours.ts still has
 * its own listPending, on a different table — they were never related.)
 */
export async function listCorrections(limit = 200): Promise<PendingCorrection[] | null> {
  if (!supabase) return [];

  const withName = `${SELECT}, profiles!time_corrections_profile_id_fkey(full_name)`;

  // TWO queries, because the cap must never be able to hide a row that is still
  // waiting on somebody. One `created_at desc` read capped at 200 drops the
  // OLDEST rows first, and the oldest pending correction is precisely the one
  // most overdue for a decision — it would vanish from the queue and from the
  // filter chips together, silently, at exactly the moment it mattered most.
  // Pending is unbounded (it is small by definition: it is a to-do list);
  // decided history is what gets capped.
  const [open, decided] = await Promise.all([
    supabase
      .from('time_corrections')
      .select(withName)
      .eq('status', 'pending')
      .order('created_at', { ascending: false }),
    supabase
      .from('time_corrections')
      .select(withName)
      .neq('status', 'pending')
      .order('created_at', { ascending: false })
      .limit(limit),
  ]);

  // `null` is NOT an empty queue. PostgREST answers an RLS-forbidden read with an
  // empty set rather than a 403, so "no rows" and "you could not read them" look
  // identical — and this screen exists because of a report that said corrections
  // were not visible. A failure has to be able to say so.
  if (open.error || decided.error) return null;

  const shape = (rows: unknown): PendingCorrection[] =>
    ((rows as (Correction & { profiles?: unknown })[]) ?? []).map((r) => {
      const p = Array.isArray(r.profiles) ? r.profiles[0] : r.profiles;
      return { ...r, full_name: (p as { full_name?: string } | null)?.full_name ?? null };
    });

  return [...shape(open.data), ...shape(decided.data)];
}

/**
 * Decide one correction. `approverUid` is sent for parity with the leave flow,
 * but the trigger overwrites it with auth.uid() — and refuses outright if the
 * decider is the subject.
 */
export async function decideCorrection(
  id: string,
  status: 'approved' | 'rejected',
  approverUid: string,
): Promise<{ error: { message: string } | null }> {
  if (!supabase) return { error: null };
  const { error } = await supabase
    .from('time_corrections')
    .update({ status, approver: approverUid, decided_at: new Date().toISOString() })
    .eq('id', id);
  return { error: error ? { message: error.message } : null };
}
